import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Avatar,
  Grid,
  Alert,
  IconButton,
  Chip,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WorkIcon from '@mui/icons-material/Work';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import Loading from '../Loading/Loading';
import { authService } from '../../services/authService';

function PiloteProfile({ userData, onUpdate }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={userData?.photoURL}
            sx={{
              width: 100,
              height: 100,
              bgcolor: 'primary.main',
              fontSize: '2rem'
            }}
          >
            {userData?.prenom?.[0]}{userData?.nom?.[0]}
          </Avatar>
        </Box>
        <Box sx={{ ml: 3 }}>
          <Typography variant="h4">
            {userData?.prenom} {userData?.nom}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {userData?.role?.nom_role}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            value={userData?.email || ''}
            disabled
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function EtudiantProfile({ userData, onUpdate, navigate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newCompetence, setNewCompetence] = useState('');
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    formation: '',
    annee: '',
    specialite: '',
    competences: [],
    description: '',
    github: '',
    linkedin: '',
    photo: '',
    telephone: '',
    cv_url: '',
    portfolio_url: ''
  });
  const [errors, setErrors] = useState({
    github: '',
    linkedin: ''
  });
  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    if (userData) {
      const initialData = {
        prenom: userData.prenom || '',
        nom: userData.nom || '',
        email: userData.email || '',
        formation: userData.etudiant?.formation || '',
        annee: userData.etudiant?.annee || '',
        specialite: userData.etudiant?.specialite || '',
        competences: userData.etudiant?.competences || [],
        description: userData.etudiant?.description || '',
        github: userData.etudiant?.github || '',
        linkedin: userData.etudiant?.linkedin || '',
        photo: userData.etudiant?.photo || '',
        telephone: userData.etudiant?.telephone || '',
        cv_url: userData.etudiant?.cv_url || '',
        portfolio_url: userData.etudiant?.portfolio_url || ''
      };
      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [userData]);

  const validateUrl = (url, type) => {
    if (!url) return true; // URL vide est valide
    
    if (type === 'github') {
      const githubRegex = /^https:\/\/github\.com\//;
      return githubRegex.test(url);
    } else if (type === 'linkedin') {
      const linkedinRegex = /^https:\/\/(?:www\.)?linkedin\.com\//;
      return linkedinRegex.test(url);
    }
    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validation en temps réel pour GitHub et LinkedIn
    if (name === 'github' || name === 'linkedin') {
      const isValid = validateUrl(value, name);
      setErrors(prev => ({
        ...prev,
        [name]: isValid ? '' : `Le lien ${name === 'github' ? 'GitHub' : 'LinkedIn'} doit commencer par https://${name === 'github' ? 'github.com/' : 'linkedin.com/'}`
      }));
    }
  };

  const handleSubmit = async () => {
    // Validation avant soumission
    const githubValid = validateUrl(formData.github, 'github');
    const linkedinValid = validateUrl(formData.linkedin, 'linkedin');

    setErrors({
      github: githubValid ? '' : 'Le lien GitHub doit commencer par https://github.com/',
      linkedin: linkedinValid ? '' : 'Le lien LinkedIn doit commencer par https://linkedin.com/'
    });

    if (!githubValid || !linkedinValid) {
      return; // Arrêter la soumission si les URLs sont invalides
    }

    try {
      const dataToSend = {
        formation: formData.formation || null,
        annee: formData.annee || null,
        description: formData.description || null,
        specialite: formData.specialite || null,
        github: formData.github || null,
        linkedin: formData.linkedin || null,
        photo: formData.photo || null
      };

      // Mise à jour du profil
      const profileResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiant/profile`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (!profileResponse.ok) {
        throw new Error('Erreur lors de la mise à jour du profil');
      }

      // Envoi des noms des compétences au lieu des IDs
      const competencesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiant/competences`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          competences: formData.competences.map(comp => comp.nom_comp)
        })
      });

      if (!competencesResponse.ok) {
        const errorData = await competencesResponse.json();
        throw new Error(errorData.message || 'Erreur lors de la mise à jour des compétences');
      }

      const responseData = await profileResponse.json();
      
      // Mise à jour du state avec les données reçues
      setFormData(prev => ({
        ...prev,
        prenom: responseData.etudiant.prenom,
        nom: responseData.etudiant.nom,
        email: responseData.etudiant.email,
        formation: responseData.etudiant.etudiant.formation || '',
        annee: responseData.etudiant.etudiant.annee || '',
        description: responseData.etudiant.etudiant.description || '',
        specialite: responseData.etudiant.etudiant.specialite || '',
        github: responseData.etudiant.etudiant.github || '',
        linkedin: responseData.etudiant.etudiant.linkedin || '',
        photo: responseData.etudiant.etudiant.photo || '',
        competences: responseData.etudiant.etudiant.competences || []
      }));

      onUpdate(responseData.etudiant);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur détaillée:', error);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET_STUDENT);

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/upload`,
        {
          method: 'POST',
          body: formDataUpload
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error("Erreur lors de l'upload de la photo");
      }

      const cloudinaryData = await cloudinaryResponse.json();

      // Créer un objet avec toutes les données actuelles plus la nouvelle photo
      const dataToSend = {
        formation: formData.formation || null,
        annee: formData.annee || null,
        description: formData.description || null,
        specialite: formData.specialite || null,
        github: formData.github || null,
        linkedin: formData.linkedin || null,
        photo: cloudinaryData.secure_url // Utiliser l'URL de la photo uploadée
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiant/profile`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(dataToSend)
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || 'Erreur lors de la mise à jour');
      }

      // Mise à jour du state avec toutes les données
      setFormData(prev => ({
        ...prev,
        prenom: responseData.etudiant.prenom,
        nom: responseData.etudiant.nom,
        email: responseData.etudiant.email,
        formation: responseData.etudiant.etudiant.formation || '',
        annee: responseData.etudiant.etudiant.annee || '',
        description: responseData.etudiant.etudiant.description || '',
        specialite: responseData.etudiant.etudiant.specialite || '',
        github: responseData.etudiant.etudiant.github || '',
        linkedin: responseData.etudiant.etudiant.linkedin || '',
        photo: responseData.etudiant.etudiant.photo || '',
        competences: responseData.etudiant.etudiant.competences || []
      }));

      onUpdate(responseData.etudiant);
    } catch (error) {
      console.error('Erreur détaillée:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleAddCompetence = async () => {
    if (newCompetence.trim()) {
      const newCompObj = {
        nom_comp: newCompetence.trim()
      };
      if (!formData.competences.some(comp => comp.nom_comp === newCompObj.nom_comp)) {
        const updatedCompetences = [...formData.competences, newCompObj];
        
        try {
          // Appel API pour mettre à jour les compétences
          const competencesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiant/competences`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            },
            body: JSON.stringify({
              competences: updatedCompetences.map(comp => comp.nom_comp)
            })
          });

          if (!competencesResponse.ok) {
            throw new Error('Erreur lors de la mise à jour des compétences');
          }

          // Mise à jour du state local et global
          setFormData(prev => ({
            ...prev,
            competences: updatedCompetences
          }));
          onUpdate({
            ...userData,
            etudiant: {
              ...userData.etudiant,
              competences: updatedCompetences
            }
          });
          setNewCompetence('');
        } catch (error) {
          console.error('Erreur lors de l\'ajout de la compétence:', error);
        }
      }
    }
  };

  const handleDeleteCompetence = async (competenceToDelete) => {
    const updatedCompetences = formData.competences.filter(
      comp => comp.nom_comp !== competenceToDelete.nom_comp
    );

    try {
      const competencesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/etudiant/competences`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          competences: updatedCompetences.map(comp => comp.nom_comp)
        })
      });

      if (!competencesResponse.ok) {
        throw new Error('Erreur lors de la suppression de la compétence');
      }

      // Mise à jour du state local et global
      setFormData(prev => ({
        ...prev,
        competences: updatedCompetences
      }));
      onUpdate({
        ...userData,
        etudiant: {
          ...userData.etudiant,
          competences: updatedCompetences
        }
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de la compétence:', error);
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setErrors({
      github: '',
      linkedin: ''
    });
    setNewCompetence('');
    setIsEditing(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={formData.photo}
            sx={{
              width: 100,
              height: 100,
              bgcolor: 'primary.main',
              fontSize: '2rem'
            }}
          >
            {formData.prenom?.[0]}{formData.nom?.[0]}
          </Avatar>
          {isEditing && (
            <IconButton
              sx={{
                position: 'absolute',
                bottom: -10,
                right: -10,
                bgcolor: 'background.paper'
              }}
              component="label"
              disabled={uploading}
            >
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={handlePhotoUpload}
              />
              <PhotoCamera />
            </IconButton>
          )}
        </Box>
        <Box sx={{ ml: 3, flex: 1 }}>
          <Typography variant="h4">
            {formData.prenom} {formData.nom}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Étudiant
          </Typography>
        </Box>
      </Box>

      <Box 
        sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 4,
          flexWrap: 'wrap'
        }}
      >
        <Button
          variant="contained"
          startIcon={<WorkIcon />}
          onClick={() => navigate('/candidatures')}
        >
          Mes Candidatures
        </Button>
        <Button
          variant="contained"
          startIcon={<FavoriteIcon />}
          onClick={() => navigate('/wishlist')}
        >
          Ma Wishlist
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Prénom"
            name="prenom"
            value={formData.prenom}
            onChange={handleChange}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nom"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Formation"
            name="formation"
            value={formData.formation}
            onChange={handleChange}
            disabled={!isEditing}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Année"
            name="annee"
            value={formData.annee}
            onChange={handleChange}
            disabled={!isEditing}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Spécialité"
            name="specialite"
            value={formData.specialite}
            onChange={handleChange}
            disabled={!isEditing}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={!isEditing}
            multiline
            rows={4}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="GitHub"
            name="github"
            value={formData.github}
            onChange={handleChange}
            disabled={!isEditing}
            error={!!errors.github}
            helperText={errors.github}
            sx={{
              '& .MuiFormHelperText-root': {
                color: theme => errors.github ? theme.palette.error.main : 'inherit'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="LinkedIn"
            name="linkedin"
            value={formData.linkedin}
            onChange={handleChange}
            disabled={!isEditing}
            error={!!errors.linkedin}
            helperText={errors.linkedin}
            sx={{
              '& .MuiFormHelperText-root': {
                color: theme => errors.linkedin ? theme.palette.error.main : 'inherit'
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Compétences
          </Typography>
          <Stack 
            direction="row" 
            spacing={1} 
            sx={{ 
              mb: 2,
              py: 2,
              gap: 1.5,
              flexWrap: 'wrap'
            }}
          >
            {formData.competences.map((competence, index) => (
              <Chip
                key={index}
                label={competence.nom_comp}
                onDelete={isEditing ? () => handleDeleteCompetence(competence) : undefined}
                color="primary"
                variant="outlined"
                sx={{
                  fontSize: '1rem',
                  height: 36,
                  '& .MuiChip-label': {
                    px: 2
                  }
                }}
              />
            ))}
          </Stack>
          {isEditing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Nouvelle compétence"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCompetence();
                  }
                }}
                size="small"
              />
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddCompetence}
                variant="contained"
                disabled={!newCompetence.trim()}
              >
                Ajouter
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {!isEditing ? (
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => setIsEditing(true)}
          >
            Modifier
          </Button>
        ) : (
          <>
            <Button
              startIcon={<CancelIcon />}
              variant="outlined"
              onClick={handleCancel}
            >
              Annuler
            </Button>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={handleSubmit}
            >
              Enregistrer
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

function Profile() {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (userRole === ROLES.ADMIN) {
      navigate('/');
      return;
    }

    const fetchUserData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/me`, {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!response.ok) throw new Error('Erreur lors du chargement du profil');

        const data = await response.json();
        setUserData(data.user);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Erreur lors du chargement du profil');
      }
    };

    fetchUserData();
  }, [currentUser, userRole, navigate]);

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!userData) {
    return <Loading />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {userRole === ROLES.PILOTE ? (
          <PiloteProfile 
            userData={userData} 
            onUpdate={setUserData} 
          />
        ) : (
          <EtudiantProfile 
            userData={userData} 
            onUpdate={setUserData}
            navigate={navigate}
          />
        )}
      </Paper>
    </Container>
  );
}

export default Profile; 